import { apiJson } from '@/api/client'

/**
 * Branches API adapter — talks to /branches + /teams on the
 * case-service. Source-of-truth schema: users.branches +
 * users.teams + users.team_members (migrations 0109/0110/0112).
 */

export type BranchType = 'hq' | 'branch' | 'agency' | 'digital' | 'regional_hub'
export type BranchStatus = 'active' | 'inactive' | 'suspended'

export interface Branch {
  public_id: string
  code: string
  name: string
  branch_type: BranchType
  is_hq: boolean
  region?: string
  city?: string
  state?: string
  country: string
  timezone: string
  phone?: string
  email?: string
  status: BranchStatus
  opened_at?: string
  closed_at?: string
  created_at: string
  updated_at: string
  user_count: number
  account_count: number
  open_alert_count: number
  open_case_count: number
  active_block_count: number
}

export interface BranchesListResponse {
  branches: Branch[]
  total: number
}

export interface CreateBranchPayload {
  code: string
  name: string
  branch_type?: BranchType
  region?: string
  city?: string
  state?: string
  country?: string
  phone?: string
  email?: string
}

export interface UpdateBranchPayload {
  name?: string
  status?: BranchStatus
  region?: string
  city?: string
  state?: string
  phone?: string
  email?: string
  branch_type?: BranchType
}

export function listBranches(): Promise<BranchesListResponse> {
  return apiJson<BranchesListResponse>('/branches')
}

export function getBranch(publicId: string): Promise<Branch> {
  return apiJson<Branch>(`/branches/${publicId}`)
}

// ---- detail drawer aggregate ---------------------------------------

export interface BranchUserDigest {
  public_id: string
  email: string
  display_name: string
  role_name?: string
  status: string
  team_name?: string
}

export interface BranchTeamDigest {
  public_id: string
  name: string
  description?: string
  lead_name?: string
  member_count: number
}

export interface BranchActivityEntry {
  kind: 'alert' | 'block'
  occurred_at: string
  headline: string
  subline?: string
  status?: string
}

export interface BranchDetail {
  branch: Branch
  users: BranchUserDigest[]
  teams: BranchTeamDigest[]
  activity: BranchActivityEntry[]
}

export function getBranchDetail(publicId: string): Promise<BranchDetail> {
  return apiJson<BranchDetail>(`/branches/${publicId}/detail`)
}

export function createBranch(payload: CreateBranchPayload): Promise<Branch> {
  return apiJson<Branch>('/branches', { method: 'POST', body: payload })
}

export function updateBranch(publicId: string, payload: UpdateBranchPayload): Promise<Branch> {
  return apiJson<Branch>(`/branches/${publicId}`, { method: 'PATCH', body: payload })
}

// ---- teams -----------------------------------------------------------

export interface TeamMember {
  user_public_id: string
  display_name: string
  email: string
  role: 'lead' | 'member'
  joined_at: string
}

export interface Team {
  public_id: string
  name: string
  description?: string
  branch_public_id: string
  branch_code: string
  branch_name: string
  lead_user_public_id?: string
  lead_display_name?: string
  member_count: number
  members?: TeamMember[]
  created_at: string
}

export interface TeamsListResponse {
  teams: Team[]
  total: number
}

export interface CreateTeamPayload {
  name: string
  description?: string
  branch_public_id: string
  lead_user_public_id?: string
}

export interface UpdateTeamPayload {
  name?: string
  description?: string
  lead_user_public_id?: string
}

export function listTeams(branchId?: string): Promise<TeamsListResponse> {
  const q = branchId ? `?branch=${encodeURIComponent(branchId)}` : ''
  return apiJson<TeamsListResponse>(`/teams${q}`)
}

export function getTeam(publicId: string): Promise<Team> {
  return apiJson<Team>(`/teams/${publicId}`)
}

export function createTeam(payload: CreateTeamPayload): Promise<{ public_id: string }> {
  return apiJson<{ public_id: string }>('/teams', { method: 'POST', body: payload })
}

export function updateTeam(publicId: string, payload: UpdateTeamPayload): Promise<{ status: string }> {
  return apiJson<{ status: string }>(`/teams/${publicId}`, { method: 'PATCH', body: payload })
}

export function addOrUpdateMember(
  teamId: string,
  payload: { user_public_id: string; role: 'lead' | 'member' },
): Promise<{ status: string }> {
  return apiJson<{ status: string }>(`/teams/${teamId}/members`, { method: 'POST', body: payload })
}

export function removeMember(teamId: string, userId: string): Promise<void> {
  return apiJson<void>(`/teams/${teamId}/members/${userId}`, { method: 'DELETE' })
}

// ---- reference data (states / countries) ---------------------------

export interface StateOption {
  code: string
  name: string
}

export interface StatesResponse {
  country: string
  states: StateOption[]
  /** Country-appropriate label: "State" / "Region" / "Province" / "County". */
  states_label: string
}

export function listStates(country?: string): Promise<StatesResponse> {
  const q = country ? `?country=${encodeURIComponent(country)}` : ''
  return apiJson<StatesResponse>(`/reference/states${q}`)
}

export interface RegionOption {
  code: string
  name: string
}

export interface RegionsResponse {
  country: string
  regions: RegionOption[]
  /** Country-appropriate label: "Geopolitical zone" / "Belt" / "Former province". */
  regions_label: string
}

export function listRegions(country?: string): Promise<RegionsResponse> {
  const q = country ? `?country=${encodeURIComponent(country)}` : ''
  return apiJson<RegionsResponse>(`/reference/regions${q}`)
}

export interface SystemConfigRegional {
  locale: string
  currency: string
  timezone: string
  business_hours: string
  country: string
  country_code: string
}

export function getSystemConfigRegional(): Promise<SystemConfigRegional> {
  // System-config carries multiple sub-blocks; we only need
  // regional. Pull the consolidated payload + slice.
  return apiJson<{ regional: SystemConfigRegional }>('/admin/system-config').then(
    (d) => d.regional,
  )
}
